/**
 * @module builder
 */

import type { PresentationSectionV2, PresentationSectionV2Option, QuoteFile } from 'paintscout'
import cloneDeep from 'lodash/cloneDeep'
import { createPresentationNextSection, insertSectionOptionTitle } from './sections'
import { replaceSectionBlockImages } from '../util/replaceSectionBlockImages'

/**
 * The single way to turn a catalog section option into a placed section.
 *
 * Every surface that offers a prefab section goes through this: the Add panel's layout-variant
 * tiles, the canvas drag-to-insert drop handler, and the Add Section dialog (both its preview tiles
 * and its confirm). #22688 was those surfaces disagreeing — the previews swapped the catalog's grey
 * placeholders for the company's photos and the inserts did not, so the section that landed never
 * looked like the tile the user dragged.
 *
 * `images` is optional, and an empty list is a no-op. That is what an account with no
 * `advanced.defaultImages` gets, and it keeps the catalog's placeholder images, as before.
 *
 * The clone matters. createPresentationNextSection only shallow-copies each block, and
 * replaceSectionImages assigns into `fields.cards[]` / `fields.files[]` in place. Without the clone,
 * building a `cards` or `gallery` section rewrites the in-memory prefabSections fixture, and every
 * later read of that catalog entry carries the previous company's photos.
 */
export function buildPresentationSectionFromOption(args: {
  sectionOption: PresentationSectionV2Option
  images?: QuoteFile[]
}): PresentationSectionV2 {
  const { sectionOption, images } = args

  const section = createPresentationNextSection({
    sectionOption: insertSectionOptionTitle(cloneDeep(sectionOption))
  })

  return replaceSectionBlockImages({ section, images: images ?? [] }).section
}
